import React, { useEffect, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';

import TableTemplate from '@templates/TableTemplate';
import FloatingButton from '@components/atoms/FloatingButton/FloatingButton';
import Button from '@components/atoms/Button/Button';
import Input from '@components/atoms/Input/Input';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import GuardedComponent from '@components/molecules/GuardedComponent/GuardedComponent';
import { COORDINATOR } from '@constants/roles';

import { fetchIndicatorTemplates } from '@services/indicatorTemplateServices';
import {
  fetchIndicatorTemplateGroupList,
  createIndicatorTemplateGroup,
  fetchIndicatorTemplateGroup,
  updateIndicatorTemplateGroup,
  deleteIndicatorTemplateGroup,
} from '@services/indicatorTemplateGroupServices';
import { clearIndicatorTemplates } from '@actions/indicatorTemplateActions';

const headCellsTemplates = [
  { type: 'name', label: 'Nazwa wzorca' },
  { type: 'category', label: 'Rodzaj wskaźnika' },
  { type: 'type', label: 'Rodzaj pomiaru' },
  { type: 'indicatorTimePeriod', label: 'Przedział czasowy' },
  { type: 'plannedNumberOfMeasurements', label: 'Planowana liczba pomiarów' },
];
// Columns for indicator template groups
const headCellsGroups = [
  { type: 'name', label: 'Nazwa grupy' },
  { type: 'templateNames', label: 'Wzorce wskaźników' },
];

const IndicatorTemplateSettings = ({ templates, fetchTemplates, clearTemplates }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [groups, setGroups] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [openAddGroup, setOpenAddGroup] = useState(false);
  const [groupName, setGroupName] = useState('');

  useEffect(() => {
    clearTemplates();
  }, [clearTemplates]);

  const triggerFetchTemplates = useCallback(() => fetchTemplates(), [fetchTemplates]);

  useEffect(() => {
    triggerFetchTemplates();
  }, [triggerFetchTemplates]);
  
  useEffect(() => {
    dispatch(fetchIndicatorTemplateGroupList())
      .then((res) => {
        const list = (res && res.data) || res || [];
        const mapped = list.map((g) => {
          let items = [];
          if (Array.isArray(g.templateNames)) {
            items = g.templateNames.map((n, i) => ({ id: i, name: n }));
          } else if (Array.isArray(g.templates)) {
            items = g.templates.map((t) => ({ id: t.id || t.indicator_id, name: t.name || t.indicator_name }));
          } else if (Array.isArray(g.templateIds)) {
            items = g.templateIds
              .map((tid) => {
                const found = templates.find((t) => (t.id || t.indicator_id) === tid);
                return found ? { id: found.id || found.indicator_id, name: found.name || found.indicator_name } : null;
              })
              .filter(Boolean);
          }
          return { ...g, templateNames: items };
        });
        setGroups(mapped);
      })
      .catch((err) => console.error(err));
  }, [dispatch, templates]);
  
  const handleAddGroup = () => {
    const templateIds = selectedTemplates.map((t) => t.id || t.indicator_id || t.value || t.id);
    dispatch(createIndicatorTemplateGroup({ name: groupName, templateIds }))
      .then(() => dispatch(fetchIndicatorTemplateGroupList()))
      .then((res) => {
        const list = res.data || res;
        setGroups(list);
        setOpenAddGroup(false);
        setGroupName('');
        setSelectedTemplates([]);
      })
      .catch((err) => console.error(err));
  };
  // Edit existing group
  const [openEditGroup, setOpenEditGroup] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupTemplates, setEditGroupTemplates] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const handleEditGroup = (group) => {
    dispatch(fetchIndicatorTemplateGroup(group.id))
      .then((res) => {
        const data = res.data || res;
        setEditingGroupId(data.id);
        setEditGroupName(data.name);
        // pre-select templates in group
        const selected = templates.filter((t) =>
          (data.templateIds || data.templates || []).includes(t.id || t.indicator_id)
        );
        setEditGroupTemplates(selected);
        setOpenEditGroup(true);
      })
      .catch((err) => console.error(err));
  };
  // Delete group
  const handleDeleteGroup = (group) => {
    // confirm deletion
    // eslint-disable-next-line no-alert
    if (window.confirm(`Czy na pewno chcesz usunąć grupę '${group.name}'?`)) {
      dispatch(deleteIndicatorTemplateGroup(group.id))
        .then(() => dispatch(fetchIndicatorTemplateGroupList()))
        .then((res) => setGroups(res.data || res))
        .catch((err) => console.error(err));
    }
  };

  return (
    <>
      <TableTemplate
        headCells={headCellsTemplates}
        data={templates}
        tableName="Wzorce wskaźników"
        /* use full configuration path for indicator templates */
        link="/konfiguracja/wzorce-wskaznikow"
        select
        selectedRows={selectedTemplates}
        updateSelected={setSelectedTemplates}
      />
      <GuardedComponent allowed_user_roles={[COORDINATOR]}>
        <Button
          variant="contained"
          disabled={selectedTemplates.length === 0}
          onClick={() => setOpenAddGroup(true)}
        >
          Stwórz grupę z wybranych wzorców
        </Button>
      </GuardedComponent>
      <GuardedComponent allowed_user_roles={[COORDINATOR]}>
        <FloatingButton onClick={() => history.push('/konfiguracja/wzorce-wskaznikow-dodaj')} />
      </GuardedComponent>
      <TableTemplate
        headCells={headCellsGroups}
        data={groups}
        tableName="Grupy wzorców wskaźników"
        rowActions={[
          { name: 'Edytuj', action: handleEditGroup },
          { name: 'Usuń', action: handleDeleteGroup },
        ]}
      />
      {/* Dialog for adding new group */}
      <Dialog open={openAddGroup} onClose={() => setOpenAddGroup(false)}>
        <DialogTitle>Dodaj grupę wzorców wskaźników</DialogTitle>
        <DialogContent>
          <Input
            label="Nazwa grupy"
            name="groupName"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setOpenAddGroup(false)}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleAddGroup}
            disabled={!groupName || selectedTemplates.length === 0}
          >
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog for editing existing group */}
      <Dialog open={openEditGroup} onClose={() => setOpenEditGroup(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edytuj grupę wzorców wskaźników</DialogTitle>
        <DialogContent>
          <Input
            label="Nazwa grupy"
            name="editGroupName"
            value={editGroupName}
            onChange={(e) => setEditGroupName(e.target.value)}
            fullWidth
          />
          <TableTemplate
            headCells={headCellsTemplates}
            data={templates}
            select
            selectedRows={editGroupTemplates}
            updateSelected={setEditGroupTemplates}
            noFilter
            noPagination
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setOpenEditGroup(false)}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              dispatch(
                updateIndicatorTemplateGroup(editingGroupId, {
                  name: editGroupName,
                  templateIds: editGroupTemplates.map((t) => t.id || t.indicator_id),
                }),
              )
                .then(() => dispatch(fetchIndicatorTemplateGroupList()))
                .then((res) => setGroups(res.data || res))
                .then(() => setOpenEditGroup(false))
                .catch((err) => console.error(err));
            }}
            disabled={!editGroupName}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

IndicatorTemplateSettings.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.any),
  fetchTemplates: PropTypes.func,
  clearTemplates: PropTypes.func,
};

IndicatorTemplateSettings.defaultProps = {
  templates: [],
  fetchTemplates: null,
  clearTemplates: null,
};

const mapStateToProps = (state) => ({
  templates: state.indicatorTemplateReducer.templates,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTemplates: () => dispatch(fetchIndicatorTemplates()),
  clearTemplates: () => dispatch(clearIndicatorTemplates()),
});

export default connect(mapStateToProps, mapDispatchToProps)(IndicatorTemplateSettings);
